import React, { useState, useContext } from 'react';
import { LanguageContext } from '../../App';
import { motion } from 'framer-motion';
import emailjs from '@emailjs/browser';

const translations = {
  en: {
    title: 'Become a Volunteer',
    subtitle: 'Join us in making a difference',
    firstName: 'First Name',
    lastName: 'Last Name',
    phone: 'Phone Number',
    phoneError: 'Please enter a valid Indian phone number',
    address: 'Address',
    pincode: 'Pincode',
    pincodeError: 'Please enter a valid 6-digit pincode',
    submit: 'Submit Application',
    success: 'Thank you for your interest! We will contact you soon.',
    error: 'Something went wrong. Please try again.',
    required: 'This field is required'
  },
  hi: {
    title: 'स्वयंसेवक बनें',
    subtitle: 'परिवर्तन लाने में हमारा साथ दें',
    firstName: 'पहला नाम',
    lastName: 'अंतिम नाम',
    phone: 'फ़ोन नंबर',
    phoneError: 'कृपया एक वैध भारतीय फोन नंबर दर्ज करें',
    address: 'पता',
    pincode: 'पिन कोड',
    pincodeError: 'कृपया एक वैध 6 अंकों का पिनकोड दर्ज करें',
    submit: 'आवेदन जमा करें',
    success: 'आपकी रुचि के लिए धन्यवाद! हम जल्द ही आपसे संपर्क करेंगे।',
    error: 'कुछ गलत हो गया। कृपया पुनः प्रयास करें।',
    required: 'यह फ़ील्ड आवश्यक है'
  }
};

interface FormData {
  firstName: string;
  lastName: string;
  phone: string;
  address: string;
  pincode: string;
}

interface FormErrors {
  firstName?: string;
  lastName?: string;
  phone?: string;
  address?: string;
  pincode?: string;
}

const VolunteerForm: React.FC = () => {
  const { language } = useContext(LanguageContext);
  const t = translations[language];

  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    phone: '',
    address: '',
    pincode: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const validateIndianPhoneNumber = (phone: string) => {
    // Remove all non-digit characters
    const cleanPhone = phone.replace(/\D/g, '');
    
    // Check if it's a valid Indian mobile number
    // Should be 10 digits
    // Should start with 6, 7, 8, or 9
    const phoneRegex = /^[6-9]\d{9}$/;
    
    return phoneRegex.test(cleanPhone);
  };

  const validatePincode = (pincode: string) => {
    // Indian pincode is exactly 6 digits
    const pincodeRegex = /^\d{6}$/;
    return pincodeRegex.test(pincode);
  };

  const validateForm = () => {
    const newErrors: FormErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = t.required;
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = t.required;
    }

    if (!formData.phone.trim()) {
      newErrors.phone = t.required;
    } else if (!validateIndianPhoneNumber(formData.phone)) {
      newErrors.phone = t.phoneError;
    }

    if (!formData.address.trim()) {
      newErrors.address = t.required;
    }

    if (!formData.pincode.trim()) {
      newErrors.pincode = t.required;
    } else if (!validatePincode(formData.pincode)) {
      newErrors.pincode = t.pincodeError;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const sendEmail = async (data: FormData) => {
    try {
      const result = await emailjs.send(
        'service_3fs983w', // Replace with your EmailJS service ID
        'template_hjejybr', // Replace with your EmailJS template ID
        {
          to_email: 'rksstrust00@gmail.com',
          from_name: `${data.firstName} ${data.lastName}`,
          phone: data.phone,
          address: data.address,
          pincode: data.pincode,
          reply_to: 'noreply@rksewa.org',
        },
        'pCK5KQez-qVBJX-F5' // Replace with your EmailJS public key
      );

      if (result.status === 200) {
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error sending email:', error);
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError('');

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const emailSent = await sendEmail(formData);
      if (emailSent) {
        setSubmitted(true);
      } else {
        setSubmitError(t.error);
      }
    } catch (error) {
      console.error('Error:', error);
      setSubmitError(t.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  if (submitted) {
    return (
      <motion.div 
        className="min-h-[60vh] flex items-center justify-center bg-gray-50"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="text-center p-8 bg-white rounded-lg shadow-lg">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <svg
              className="w-16 h-16 mx-auto text-green-500 mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </motion.div>
          <h2 className="text-2xl font-bold text-primary mb-4">{t.success}</h2>
        </div>
      </motion.div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container-custom">
        <div className="max-w-2xl mx-auto">
          <motion.div 
            className="bg-white rounded-lg shadow-lg p-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-3xl font-bold text-center mb-2">{t.title}</h1>
            <p className="text-gray-600 text-center mb-8">{t.subtitle}</p>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                    {t.firstName}
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    required
                    value={formData.firstName}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-primary focus:border-transparent ${
                      errors.firstName ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.firstName && (
                    <p className="mt-1 text-sm text-red-500">{errors.firstName}</p>
                  )}
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                    {t.lastName}
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    required
                    value={formData.lastName}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-primary focus:border-transparent ${
                      errors.lastName ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.lastName && (
                    <p className="mt-1 text-sm text-red-500">{errors.lastName}</p>
                  )}
                </div>
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                  {t.phone}
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-primary focus:border-transparent ${
                    errors.phone ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.phone && (
                  <p className="mt-1 text-sm text-red-500">{errors.phone}</p>
                )}
              </div>

              <div>
                <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
                  {t.address}
                </label>
                <textarea
                  id="address"
                  name="address"
                  required
                  value={formData.address}
                  onChange={handleChange}
                  rows={3}
                  className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-primary focus:border-transparent ${
                    errors.address ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.address && (
                  <p className="mt-1 text-sm text-red-500">{errors.address}</p>
                )}
              </div>

              <div>
                <label htmlFor="pincode" className="block text-sm font-medium text-gray-700 mb-1">
                  {t.pincode}
                </label>
                <input
                  type="text"
                  id="pincode"
                  name="pincode"
                  required
                  value={formData.pincode}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-primary focus:border-transparent ${
                    errors.pincode ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.pincode && (
                  <p className="mt-1 text-sm text-red-500">{errors.pincode}</p>
                )}
              </div>

              {submitError && (
                <div className="text-red-500 text-center">{submitError}</div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full btn-primary py-3 ${
                  isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {t.submit}
                  </span>
                ) : (
                  t.submit
                )}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default VolunteerForm; 